import { AppBreadcrumb, AppCard, AppSpace, AppSpin } from '@/components/atoms';
import { CreateClinicUserForm } from '@/components/organisms';
import { useAdminClinic } from '@/hooks/admin/useAdminClinics';
import { useCreateClinicUserMutation } from '@/hooks/admin/useCreateClinicUserMutation';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import type { ClinicUserFormData, ClinicUserEditFormData } from '@/schemas/clinicUserSchema';
import useAppToast from '@/hooks/useAppToast';
import { AxiosError } from 'axios';
import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeOptionalDate } from '@/utils/dateConverter';

type ApiFieldErrors = Partial<Record<keyof ClinicUserFormData, string>>;

/** Error code returned by backend when clinic does not exist or is not accessible (create clinic user). */
export const CLINIC_NOT_FOUND_OR_ACCESS_DENIED = 'CLINIC_NOT_FOUND_OR_ACCESS_DENIED';

interface ApiValidationErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

const CreateClinicUserPage: React.FC = () => {
  const toast = useAppToast();
  const navigate = useNavigate();
  const { site } = useParams<{ site: string }>();
  const [searchParams] = useSearchParams();
  const { currentSite } = useSiteAdminAuth();
  const siteType = site ?? currentSite ?? '';
  const clinicIdParam = searchParams.get('clinic_id');
  const clinicId = clinicIdParam ? parseInt(clinicIdParam, 10) : 0;

  const {
    data: clinicData,
    isError: isClinicError,
    isPending: isClinicPending,
  } = useAdminClinic(siteType, clinicId);
  const createUserMutation = useCreateClinicUserMutation();
  const [apiErrors, setApiErrors] = useState<ApiFieldErrors>({});
  const [clinicIdErrorOccurred, setClinicIdErrorOccurred] = useState(false);
  const queryClient = useQueryClient();

  const isInvalidParam = clinicIdParam !== null && (Number.isNaN(clinicId) || clinicId <= 0);
  const clinicNotFound = clinicIdParam !== null && clinicId > 0 && isClinicError;
  const hasValidClinic = clinicId > 0 && !isInvalidParam && !clinicNotFound && !!clinicData;
  const hasUsableClinicIdParam = clinicIdParam !== null && !clinicNotFound && !isInvalidParam;
  const shouldWaitForClinicCheck = clinicIdParam !== null && clinicId > 0;
  const waitForClinicCheck = shouldWaitForClinicCheck && isClinicPending;
  const clinicOptionsEnabled = !waitForClinicCheck;

  const lockedClinicDisplayName = useMemo(() => {
    if (!hasValidClinic || !clinicData?.data) return null;
    const data: any = clinicData.data;
    const infos = Array.isArray(data.clinic_informations) ? data.clinic_informations : null;
    if (infos && infos.length > 0) {
      const latest = infos.reduce(
        (max: any, curr: any) => (curr.id > max.id ? curr : max),
        infos[0]
      );
      return latest?.name ?? data.clinic_number ?? null;
    }
    return data.clinic_number ?? null;
  }, [hasValidClinic, clinicData]);

  const breadcrumbItems = [
    {
      title: 'Home',
      href: `/${siteType}/admin`,
    },
    {
      title: '治療院詳細',
      href: `/${siteType}/admin/clinics/${clinicId}`,
    },
    {
      title: '新規',
    },
  ];

  const handleSubmit = async (formData: ClinicUserFormData | ClinicUserEditFormData) => {
    if (!siteType) {
      toast.error('サイト情報が取得できませんでした。');
      return;
    }

    setApiErrors({});

    try {
      const response = await createUserMutation.mutateAsync({
        siteType,
        data: {
          clinic_id: formData.clinic_id,
          role: formData.role,
          login_id: formData.login_id,
          password: formData.password ?? '',
          name: formData.name,
          email: formData.email && formData.email.trim() !== '' ? formData.email : null,
          available_from: normalizeOptionalDate(formData.available_from),
          available_to: normalizeOptionalDate(formData.available_to),
        },
      });
      toast.success('更新しました。');
      queryClient.invalidateQueries({ queryKey: ['clinic-detail', String(clinicId), siteType] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'clinics', siteType, formData.clinic_id, 'clinic-admin'],
      });
      const newUserId = response.data.id;
      navigate(`/${siteType}/admin/clinic_users/${newUserId}/edit`, { replace: true });
    } catch (err) {
      const axiosError = err as AxiosError<ApiValidationErrorResponse>;
      const responseErrors = axiosError.response?.data?.errors;
      const status = axiosError.response?.status;

      if (responseErrors) {
        const fieldErrors: ApiFieldErrors = {};
        const errorCode = responseErrors.error_code?.[0];
        const isClinicNotFoundOrAccessDenied =
          errorCode === CLINIC_NOT_FOUND_OR_ACCESS_DENIED ||
          (responseErrors.clinic_id && responseErrors.clinic_id.length > 0);

        for (const [field, messages] of Object.entries(responseErrors)) {
          if (messages && messages.length > 0 && field !== 'clinic_id' && field !== 'error_code') {
            fieldErrors[field as keyof ClinicUserFormData] = messages[0];
          }
        }

        if (isClinicNotFoundOrAccessDenied) {
          const message =
            responseErrors.clinic_id?.[0] ??
            '指定された治療院は存在しません。他の治療院を選んでください。';
          toast.error(message);
          setClinicIdErrorOccurred(true);
        } else if (Object.keys(fieldErrors).length === 0) {
          toast.error('エラーが発生しました。');
        }
        setApiErrors(fieldErrors);
      } else if (status === 403 && formData.clinic_id) {
        toast.error('指定された治療院は存在しません。他の治療院を選んでください。');
        setClinicIdErrorOccurred(true);
      } else {
        toast.error('エラーが発生しました。');
      }
    }
  };

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <AppBreadcrumb items={breadcrumbItems} />
        <AppCard title="治療院ユーザー作成" className="w-full">
          {waitForClinicCheck ? (
            <div className="flex min-h-40 items-center justify-center">
              <AppSpin size="large" />
            </div>
          ) : (
            <CreateClinicUserForm
              siteType={siteType}
              clinicId={hasValidClinic ? clinicId : undefined}
              // Lock clinic only when it is valid and no clinic_id error has occurred.
              isClinicLocked={hasValidClinic && !clinicIdErrorOccurred}
              hasClinicIdParam={hasUsableClinicIdParam}
              clinicOptionsEnabled={clinicOptionsEnabled}
              waitForClinicCheck={waitForClinicCheck}
              lockedClinicDisplayName={lockedClinicDisplayName}
              clinicSiteType={clinicData?.data?.site_type ?? null}
              onSubmit={handleSubmit}
              onCancel={
                hasValidClinic
                  ? () => navigate(`/${siteType}/admin/clinics/${clinicId}`)
                  : undefined
              }
              isLoading={createUserMutation.isPending}
              apiErrors={apiErrors}
              resetClinicToFirstAndDisable={clinicIdErrorOccurred}
            />
          )}
        </AppCard>
      </AppSpace>
    </div>
  );
};

export default CreateClinicUserPage;
